import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiPlus, FiRefreshCw, FiPhone, FiBook,
    FiClock, FiChevronDown, FiChevronRight, FiX, FiSave,
    FiTrash2, FiEdit2, FiArrowRight, FiAlertTriangle,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import admissionService from "../../services/admissionService";
import courseService from "../../services/courseService";

// ─── Constants ────────────────────────────────────────────────────────────────
const ACTIVE_STAGES = ["Enquiry", "Applied", "Admitted", "Enrolled"];
const EXIT_STAGES = ["Rejected", "Withdrawn"];
const SOURCES = ["Walk-in", "Referral", "Online", "Phone", "Other"];
const GENDERS = ["Male", "Female", "Other"];
const CATEGORIES = ["General", "OBC", "SC", "ST", "NT", "EWS", "Other"];
const ADM_CATS = ["Merit", "Management", "Lateral Entry", "Other"];

const STAGE_CONFIG = {
    Enquiry: { color: "#003b67", bg: "#e8f1f8", label: "Enquiry" },
    Applied: { color: "#d97706", bg: "#fef3c7", label: "Applied" },
    Admitted: { color: "#7c3aed", bg: "#ede9fe", label: "Admitted" },
    Enrolled: { color: "#16a34a", bg: "#dcfce7", label: "Enrolled" },
    Rejected: { color: "#dc2626", bg: "#fee2e2", label: "Rejected" },
    Withdrawn: { color: "#6b7280", bg: "#f3f4f6", label: "Withdrawn" },
};

const ALLOWED_TRANSITIONS = {
    Enquiry: ["Applied", "Rejected", "Withdrawn"],
    Applied: ["Admitted", "Rejected", "Withdrawn"],
    Admitted: ["Enrolled", "Rejected", "Withdrawn"],
    Enrolled: [],
};

// Days since a date string
const daysSince = (dateStr) => {
    if (!dateStr) return null;
    const diff = Date.now() - new Date(dateStr).getTime();
    return Math.floor(diff / 86400000);
};

// ─── Stage Move Modal ─────────────────────────────────────────────────────────
function StageMoveModal({ admission, targetStage, onClose, onDone }) {
    const [form, setForm] = useState({ stage: targetStage });
    const [loading, setLoading] = useState(false);

    const today = new Date().toISOString().slice(0, 10);
    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const handleSave = async () => {
        setLoading(true);
        try {
            await admissionService.moveToStage(admission._id, form);
            toast.success(`Moved to ${targetStage}.`);
            onDone();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" style={{ width: 520 }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
                    <h3 style={{ margin: 0 }}>Move to <span style={{ color: STAGE_CONFIG[targetStage]?.color }}>{targetStage}</span></h3>
                    <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><FiX size={18} /></button>
                </div>
                <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: "1rem" }}>
                    {admission.fullName} — {admission.courseInterested}
                </p>

                <div style={{ display: "grid", gap: "0.75rem" }}>
                    {/* Applied */}
                    {targetStage === "Applied" && (<>
                        <div className="form-group">
                            <label className="form-label">Application Date *</label>
                            <input type="date" className="form-control" defaultValue={today} onChange={(e) => set("applicationDate", e.target.value)} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
                            <div className="form-group">
                                <label className="form-label">Gender</label>
                                <select className="form-control" onChange={(e) => set("gender", e.target.value)}>
                                    <option value="">Select…</option>
                                    {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Date of Birth</label>
                                <input type="date" className="form-control" onChange={(e) => set("dob", e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Category</label>
                                <select className="form-control" defaultValue="General" onChange={(e) => set("category", e.target.value)}>
                                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Previous %</label>
                                <input type="number" min="0" max="100" className="form-control" placeholder="e.g. 75" onChange={(e) => set("previousPercentage", e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Entrance Score</label>
                                <input type="number" min="0" className="form-control" placeholder="optional" onChange={(e) => set("entranceScore", e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Parent Mobile</label>
                                <input type="tel" className="form-control" placeholder="10 digits" onChange={(e) => set("parentMobile", e.target.value)} />
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Father's Name</label>
                            <input className="form-control" onChange={(e) => set("fatherName", e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Mother's Name</label>
                            <input className="form-control" onChange={(e) => set("motherName", e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Previous Institution</label>
                            <input className="form-control" onChange={(e) => set("previousInstitution", e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Application Notes</label>
                            <textarea rows={2} className="form-control" style={{ resize: "vertical" }} onChange={(e) => set("applicationNotes", e.target.value)} />
                        </div>
                    </>)}

                    {/* Admitted */}
                    {targetStage === "Admitted" && (<>
                        <div className="form-group">
                            <label className="form-label">Admission Date *</label>
                            <input type="date" className="form-control" defaultValue={today} onChange={(e) => set("admissionDate", e.target.value)} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
                            <div className="form-group">
                                <label className="form-label">Admission Category</label>
                                <select className="form-control" onChange={(e) => set("admissionCategory", e.target.value)}>
                                    <option value="">Select…</option>
                                    {ADM_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Seat No.</label>
                                <input className="form-control" placeholder="optional" onChange={(e) => set("seatNumber", e.target.value)} />
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Admission Notes</label>
                            <textarea rows={2} className="form-control" style={{ resize: "vertical" }} onChange={(e) => set("admissionNotes", e.target.value)} />
                        </div>
                    </>)}

                    {/* Enrolled */}
                    {targetStage === "Enrolled" && (<>
                        <div className="form-group">
                            <label className="form-label">Enrolled Date</label>
                            <input type="date" className="form-control" defaultValue={today} onChange={(e) => set("enrolledDate", e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Enrollment Notes</label>
                            <textarea rows={2} className="form-control" style={{ resize: "vertical" }} onChange={(e) => set("enrollmentNotes", e.target.value)} />
                        </div>
                    </>)}

                    {/* Rejected / Withdrawn */}
                    {(targetStage === "Rejected" || targetStage === "Withdrawn") && (<>
                        <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.75rem 1rem", fontSize: 13, color: "#92400e", display: "flex", gap: 8 }}>
                            <FiAlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                            This action is permanent and cannot be undone.
                        </div>
                        <div className="form-group">
                            <label className="form-label">Reason for {targetStage}</label>
                            <textarea rows={3} className="form-control" style={{ resize: "vertical" }} placeholder="Reason / remarks…" onChange={(e) => set("exitReason", e.target.value)} />
                        </div>
                    </>)}
                </div>

                <div className="modal-actions" style={{ marginTop: "1.25rem" }}>
                    <button className="btn btn-outline" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleSave} disabled={loading}
                        style={{ background: STAGE_CONFIG[targetStage]?.color, borderColor: STAGE_CONFIG[targetStage]?.color }}>
                        <FiArrowRight size={13} /> {loading ? "Moving…" : `Move to ${targetStage}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Admission Card ───────────────────────────────────────────────────────────
function AdmissionCard({ admission, onMoveRequest, onDelete, onEdit, isDragOver }) {
    const days = daysSince(admission.enquiryDate);
    const nexts = ALLOWED_TRANSITIONS[admission.stage] || [];

    return (
        <div
            draggable
            onDragStart={(e) => e.dataTransfer.setData("admissionId", admission._id)}
            style={{
                background: "#fff", border: "1px solid var(--border)", borderRadius: 10,
                padding: "0.85rem", marginBottom: "0.6rem", cursor: "grab",
                boxShadow: isDragOver ? "0 0 0 2px #003b67" : "0 1px 3px rgba(0,0,0,0.06)",
                transition: "box-shadow 0.15s",
            }}
        >
            {/* Name + course */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.3 }}>{admission.fullName}</div>
                <div style={{ display: "flex", gap: "0.25rem", flexShrink: 0, marginLeft: 6 }}>
                    <button title="Edit" onClick={() => onEdit(admission)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 3 }}>
                        <FiEdit2 size={12} />
                    </button>
                    <button title="Delete" onClick={() => onDelete(admission)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", padding: 3 }}>
                        <FiTrash2 size={12} />
                    </button>
                </div>
            </div>

            {/* Meta */}
            <div style={{ fontSize: 12, color: "var(--text-muted)", display: "grid", gap: 3 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}><FiPhone size={11} /> {admission.mobile}</span>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}><FiBook size={11} /> {admission.courseInterested}</span>
                {days !== null && (
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}><FiClock size={11} /> {days === 0 ? "Today" : `${days}d ago`}</span>
                )}
            </div>

            {/* Source badge */}
            <div style={{ marginTop: 8, display: "flex", gap: "0.35rem", flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: 10.5, background: "#f1f5f9", color: "#64748b", padding: "1px 7px", borderRadius: 99 }}>{admission.source}</span>

                {/* Move buttons — only for active (non-terminal) cards */}
                {nexts.filter((s) => !EXIT_STAGES.includes(s)).map((s) => (
                    <button key={s} onClick={() => onMoveRequest(admission, s)}
                        style={{ fontSize: 10.5, background: STAGE_CONFIG[s]?.bg, color: STAGE_CONFIG[s]?.color, border: "none", borderRadius: 99, padding: "2px 8px", cursor: "pointer", fontWeight: 600 }}>
                        → {s}
                    </button>
                ))}
                {nexts.some((s) => EXIT_STAGES.includes(s)) && (
                    <div style={{ position: "relative", display: "inline-block" }}>
                        <select
                            defaultValue=""
                            onChange={(e) => { if (e.target.value) { onMoveRequest(admission, e.target.value); e.target.value = ""; } }}
                            style={{ fontSize: 10.5, background: "#f3f4f6", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 99, padding: "2px 8px", cursor: "pointer" }}
                        >
                            <option value="">Exit…</option>
                            {nexts.filter((s) => EXIT_STAGES.includes(s)).map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Kanban Column ────────────────────────────────────────────────────────────
function KanbanColumn({ stage, cards, count, onDrop, onMoveRequest, onDelete, onEdit, onAddEnquiry }) {
    const [dragOver, setDragOver] = useState(false);
    const cfg = STAGE_CONFIG[stage];

    const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
    const handleDragLeave = () => setDragOver(false);
    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const id = e.dataTransfer.getData("admissionId");
        if (id) onDrop(id, stage);
    };

    return (
        <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
                minWidth: 270, flex: "1 1 270px",
                background: dragOver ? `${cfg.color}08` : "#f8fafc",
                border: dragOver ? `2px dashed ${cfg.color}` : "2px solid transparent",
                borderRadius: 12, padding: "0.75rem",
                transition: "border 0.15s, background 0.15s",
            }}
        >
            {/* Column header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: cfg.color }} />
                    <span style={{ fontWeight: 700, fontSize: 14, color: cfg.color }}>{cfg.label}</span>
                    <span style={{ fontSize: 11.5, background: cfg.bg, color: cfg.color, padding: "1px 8px", borderRadius: 99, fontWeight: 700 }}>{count}</span>
                </div>
                {stage === "Enquiry" && (
                    <button onClick={onAddEnquiry} title="Add enquiry"
                        style={{ background: cfg.bg, color: cfg.color, border: "none", borderRadius: 8, width: 28, height: 28, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FiPlus size={14} />
                    </button>
                )}
            </div>

            {/* Cards */}
            <div style={{ minHeight: 80 }}>
                {cards.length === 0 ? (
                    <div style={{ textAlign: "center", color: "#cbd5e1", fontSize: 12.5, padding: "1rem 0", fontStyle: "italic" }}>
                        {dragOver ? "Drop here" : "Empty"}
                    </div>
                ) : (
                    cards.map((a) => (
                        <AdmissionCard key={a._id} admission={a} onMoveRequest={onMoveRequest}
                            onDelete={onDelete} onEdit={onEdit} />
                    ))
                )}
            </div>
        </div>
    );
}

// ─── Quick Enquiry Form ───────────────────────────────────────────────────────
function QuickEnquiryModal({ academicYearId, courses, onClose, onDone }) {
    const [form, setForm] = useState({ courseInterested: courses[0]?.shortName || "", source: "Walk-in" });
    const [loading, setLoading] = useState(false);
    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const handleSave = async () => {
        if (!form.fullName || !form.mobile) return toast.error("Name and mobile are required.");
        if (!/^\d{10}$/.test(form.mobile)) return toast.error("Mobile must be 10 digits.");
        if (!form.courseInterested) return toast.error("Please select a course.");
        setLoading(true);
        try {
            await admissionService.create({ ...form, academicYear: academicYearId });
            toast.success("Enquiry added.");
            onDone();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
                    <h3 style={{ margin: 0 }}>New Enquiry</h3>
                    <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><FiX size={18} /></button>
                </div>
                <div style={{ display: "grid", gap: "0.75rem" }}>
                    <div className="form-group">
                        <label className="form-label">Full Name *</label>
                        <input className="form-control" autoFocus onChange={(e) => set("fullName", e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Mobile *</label>
                        <input type="tel" className="form-control" maxLength={10} onChange={(e) => set("mobile", e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Email</label>
                        <input type="email" className="form-control" onChange={(e) => set("email", e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Course Interested *</label>
                        <select className="form-control" value={form.courseInterested} onChange={(e) => set("courseInterested", e.target.value)}>
                            <option value="">Select course</option>
                            {courses.map((course) => (
                                <option key={course._id} value={course.shortName}>
                                    {course.shortName}
                                </option>
                            ))}
                        </select>
                        {courses.length === 0 && (
                            <span className="form-hint">Add an active course under Settings first.</span>
                        )}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
                        <div className="form-group">
                            <label className="form-label">Source</label>
                            <select className="form-control" value={form.source} onChange={(e) => set("source", e.target.value)}>
                                {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Enquiry Date</label>
                            <input type="date" className="form-control" defaultValue={new Date().toISOString().slice(0, 10)} onChange={(e) => set("enquiryDate", e.target.value)} />
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Notes</label>
                        <textarea rows={2} className="form-control" style={{ resize: "vertical" }} onChange={(e) => set("enquiryNotes", e.target.value)} />
                    </div>
                </div>
                <div className="modal-actions" style={{ marginTop: "1.25rem" }}>
                    <button className="btn btn-outline" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleSave} disabled={loading || courses.length === 0}>
                        <FiSave size={13} /> {loading ? "Saving…" : "Add Enquiry"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Main Board ───────────────────────────────────────────────────────────────
export default function AdmissionBoard() {
    const { academicYear } = useApp();
    const navigate = useNavigate();

    const [admissions, setAdmissions] = useState([]);
    const [stats, setStats] = useState({});
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showExited, setShowExited] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Modals
    const [enquiryModal, setEnquiryModal] = useState(false);
    const [moveModal, setMoveModal] = useState(null);  // { admission, targetStage }
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetch = useCallback(async () => {
        if (!academicYear?._id) { setLoading(false); return; }
        setLoading(true);
        try {
            const [admRes, statsRes] = await Promise.all([
                admissionService.getAll({ academicYear: academicYear._id, includeExited: "true", limit: 500 }),
                admissionService.getStats({ academicYear: academicYear._id }),
            ]);
            setAdmissions(admRes.data || []);
            setStats(statsRes.data || {});
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [academicYear]);

    useEffect(() => { fetch(); }, [fetch]);

    useEffect(() => {
        courseService.getAll({ isActive: true })
            .then((res) => setCourses(res.data || []))
            .catch((err) => toast.error(err.response?.data?.message || err.message));
    }, []);

    // Filter by search
    const filtered = admissions.filter((a) => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        return a.fullName?.toLowerCase().includes(q) || a.mobile?.includes(q) || a.email?.toLowerCase().includes(q);
    });

    const byStage = (stage) => filtered.filter((a) => a.stage === stage);

    // Drag drop
    const handleDrop = (admissionId, targetStage) => {
        const admission = admissions.find((a) => a._id === admissionId);
        if (!admission) return;
        const allowed = ALLOWED_TRANSITIONS[admission.stage] || [];
        if (!allowed.includes(targetStage)) {
            toast.error(`Cannot move from ${admission.stage} to ${targetStage}.`);
            return;
        }
        setMoveModal({ admission, targetStage });
    };

    // Delete
    const handleDelete = async () => {
        setDeleting(true);
        try {
            await admissionService.remove(deleteTarget._id);
            toast.success("Deleted.");
            setDeleteTarget(null);
            fetch();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setDeleting(false);
        }
    };

    if (!academicYear?._id) return (
        <div>
            <div className="page-header"><h1 className="page-title">Admission Board</h1></div>
            <div className="card"><div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>Select an academic year from the header to view admissions.</div></div>
        </div>
    );

    const totalActive = ACTIVE_STAGES.reduce((s, st) => s + (stats[st] || 0), 0);

    return (
        <div>
            {/* Header */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
                <div>
                    <h1 className="page-title">Admission Board</h1>
                    <p className="page-subtitle">{academicYear.label} · {totalActive} active</p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <input className="form-control" style={{ width: 220, fontSize: 13 }}
                        placeholder="Search name, mobile…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)} />
                    <button className="btn btn-outline" onClick={fetch}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <FiRefreshCw size={13} />
                    </button>
                    <button className="btn btn-primary" onClick={() => setEnquiryModal(true)}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <FiPlus size={14} /> New Enquiry
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="loading-center"><div className="spinner" />Loading admissions…</div>
            ) : (
                <>
                    {/* Kanban columns */}
                    <div style={{ display: "flex", gap: "0.85rem", overflowX: "auto", paddingBottom: "1rem", alignItems: "flex-start" }}>
                        {ACTIVE_STAGES.map((stage) => (
                            <KanbanColumn
                                key={stage}
                                stage={stage}
                                cards={byStage(stage)}
                                count={stats[stage] || 0}
                                onDrop={handleDrop}
                                onMoveRequest={(a, s) => setMoveModal({ admission: a, targetStage: s })}
                                onDelete={setDeleteTarget}
                                onEdit={(a) => navigate(`/admissions/${a._id}/edit`)}
                                onAddEnquiry={() => setEnquiryModal(true)}
                            />
                        ))}
                    </div>

                    {/* Closed / Exited section */}
                    <div style={{ marginTop: "0.5rem" }}>
                        <button
                            onClick={() => setShowExited((p) => !p)}
                            style={{ display: "flex", alignItems: "center", gap: "0.4rem", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--text-muted)", padding: "0.4rem 0", marginBottom: "0.5rem" }}
                        >
                            {showExited ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                            Closed ({(stats.Rejected || 0) + (stats.Withdrawn || 0)})
                        </button>

                        {showExited && (
                            <div style={{ display: "flex", gap: "0.85rem", overflowX: "auto" }}>
                                {EXIT_STAGES.map((stage) => (
                                    <KanbanColumn
                                        key={stage}
                                        stage={stage}
                                        cards={byStage(stage)}
                                        count={stats[stage] || 0}
                                        onDrop={() => { }}
                                        onMoveRequest={() => { }}
                                        onDelete={setDeleteTarget}
                                        onEdit={(a) => navigate(`/admissions/${a._id}/edit`)}
                                        onAddEnquiry={() => { }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Modals */}
            {enquiryModal && (
                <QuickEnquiryModal
                    academicYearId={academicYear._id}
                    courses={courses}
                    onClose={() => setEnquiryModal(false)}
                    onDone={fetch}
                />
            )}

            {moveModal && (
                <StageMoveModal
                    admission={moveModal.admission}
                    targetStage={moveModal.targetStage}
                    onClose={() => setMoveModal(null)}
                    onDone={fetch}
                />
            )}

            {deleteTarget && (
                <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="modal" style={{ width: 380 }} onClick={(e) => e.stopPropagation()}>
                        <h3>Delete Admission Record?</h3>
                        <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
                            Permanently delete the record for <strong>{deleteTarget.fullName}</strong>? This cannot be undone.
                        </p>
                        <div className="modal-actions">
                            <button className="btn btn-outline" onClick={() => setDeleteTarget(null)}>Cancel</button>
                            <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                                <FiTrash2 size={13} /> {deleting ? "Deleting…" : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
